import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Bot,
  Activity,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  RefreshCw,
  Wrench,
  Zap,
  Clock,
  PlayCircle,
  PauseCircle,
  TrendingUp,
  Database,
  Cloud,
  XCircle,
  FileX,
} from 'lucide-react';

const POLL_INTERVAL_MS = 15000; // 15s — bot itself runs every 60s

const SEVERITY_STYLES = {
  info:     { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Activity,        ring: 'ring-blue-400/40' },
  warning:  { color: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertTriangle, ring: 'ring-amber-400/40' },
  critical: { color: 'bg-red-100 text-red-800 border-red-300', icon: ShieldAlert,         ring: 'ring-red-400/40' },
};

const STATUS_STYLES = {
  healthy:  { color: 'bg-emerald-500',  text: 'All systems healthy',     glow: 'shadow-emerald-500/30' },
  degraded: { color: 'bg-amber-500',    text: 'Issues detected',         glow: 'shadow-amber-500/30' },
  critical: { color: 'bg-red-500',      text: 'Critical issues',         glow: 'shadow-red-500/30' },
  idle:     { color: 'bg-slate-400',    text: 'Bot idle',                glow: 'shadow-slate-400/20' },
};

const ISSUE_LABELS = {
  stale_processing_flag:   { label: 'Stale Processing Flag', icon: Database },
  stuck_processing_calls:  { label: 'Stuck Calls (>10 min)', icon: Clock },
  orphan_pending_calls:    { label: 'Orphan Pending Calls', icon: AlertTriangle },
  onedrive_token_expiry:   { label: 'OneDrive Token Expiry', icon: Cloud },
  processor_no_heartbeat:  { label: 'Processor No Heartbeat', icon: Activity },
  rate_limit_storm:        { label: 'Rate-Limit Storm', icon: ShieldAlert },
  onedrive_sync_stuck:     { label: 'OneDrive Sync Stuck', icon: Cloud },
  queue_drift:             { label: 'Queue Drift', icon: Database },
};

const apiBase = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  // The app stores the JWT under 'saas_token' (see AuthContext.jsx).
  // Fall back to 'token' for any legacy path.
  return localStorage.getItem('saas_token') || localStorage.getItem('token') || '';
}

async function apiGet(path) {
  const res = await fetch(`${apiBase}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function apiPost(path) {
  const res = await fetch(`${apiBase}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function formatRelative(iso) {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

function StatusCard({ status, lastRun, statsObj, isEnabled, onToggle }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.idle;
  return (
    <Card data-testid="bot-status-card" className={`border-2 ${s.color.replace('bg-', 'border-')}/40 shadow-lg ${s.glow}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className={`relative h-14 w-14 rounded-2xl ${s.color} flex items-center justify-center shadow-lg ${s.glow}`}>
              <Bot className="h-7 w-7 text-white" />
              {isEnabled && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Pipeline Healing Bot</h2>
                <Badge variant="outline" className={`${s.color} text-white border-transparent`}>{s.text}</Badge>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Last check {formatRelative(lastRun)} · runs every 60s · {statsObj?.total_runs ?? 0} total scans · {statsObj?.total_auto_fixes ?? 0} auto-fixes · {statsObj?.total_manual_fixes ?? 0} manual fixes
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Auto-heal</span>
            <Switch data-testid="bot-toggle" checked={isEnabled} onCheckedChange={onToggle} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function IssueCard({ issue, onHeal, isHealing }) {
  const sev = SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.info;
  const meta = ISSUE_LABELS[issue.issue_id] || { label: issue.issue_id, icon: AlertTriangle };
  const SevIcon = sev.icon;
  const MetaIcon = meta.icon;
  return (
    <Card data-testid={`issue-card-${issue.issue_id}`} className={`border-l-4 ${sev.color.replace('bg-', 'border-l-').replace('100', '500')} hover:shadow-md transition-shadow`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <MetaIcon className="h-4 w-4 text-slate-500" />
              <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {meta.label}
              </span>
              <Badge className={`${sev.color} border ml-2 capitalize`}>
                <SevIcon className="h-3 w-3 mr-1" />{issue.severity}
              </Badge>
              {issue.auto_fixable ? (
                <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50">
                  <Zap className="h-3 w-3 mr-1" />auto-heal
                </Badge>
              ) : (
                <Badge variant="outline" className="text-orange-700 border-orange-300 bg-orange-50">
                  needs confirmation
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">{issue.title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{issue.description}</p>
          </div>
          <Button
            data-testid={`heal-btn-${issue.issue_id}`}
            size="sm"
            variant={issue.auto_fixable ? 'outline' : 'default'}
            disabled={isHealing}
            onClick={() => onHeal(issue.issue_id)}
            className="shrink-0"
          >
            {isHealing ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Wrench className="h-4 w-4 mr-1" />}
            {issue.auto_fixable ? 'Re-run fix' : 'Confirm fix'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ActivityRow({ ev }) {
  const sev = SEVERITY_STYLES[ev.severity] || SEVERITY_STYLES.info;
  const meta = ISSUE_LABELS[ev.issue_id] || { label: ev.issue_id, icon: Activity };
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className={`h-8 w-8 rounded-lg ${sev.color} flex items-center justify-center shrink-0`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{ev.title}</p>
          <Badge variant="outline" className="text-[10px] py-0 px-1.5 capitalize">{ev.trigger}</Badge>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          {meta.label} · {formatRelative(ev.timestamp)}
          {ev.details?.count != null && <span className="ml-1">· {ev.details.count} affected</span>}
        </p>
      </div>
    </div>
  );
}

export default function AdminPipelineBot() {
  const [status, setStatus]       = useState(null);
  const [diagnostics, setDiag]    = useState(null);
  const [activity, setActivity]   = useState([]);
  const [failures, setFailures]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [healingId, setHealingId] = useState(null);
  const [runningFix, setRunningFix] = useState(false);
  const pollRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [st, di, ac, fa] = await Promise.all([
        apiGet('/api/pipeline-bot/status'),
        apiGet('/api/pipeline-bot/diagnostics'),
        apiGet('/api/pipeline-bot/activity?limit=100'),
        apiGet('/api/pipeline-bot/recent-failures?limit=20'),
      ]);
      setStatus(st);
      setDiag(di);
      setActivity(ac.events || []);
      setFailures(fa);
    } catch (e) {
      console.error('Bot fetch failed:', e);
      toast.error(`Failed to refresh bot status: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    pollRef.current = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchAll]);

  const handleHeal = async (issueId) => {
    setHealingId(issueId);
    try {
      const r = await apiPost(`/api/pipeline-bot/heal/${issueId}`);
      if (r.fixed) {
        toast.success(r.title || `${issueId} healed`);
      } else {
        toast.warning(r.title || `Heal completed with warnings`);
      }
      await fetchAll();
    } catch (e) {
      toast.error(`Heal failed: ${e.message}`);
    } finally {
      setHealingId(null);
    }
  };

  const handleAutoHealAll = async () => {
    setRunningFix(true);
    try {
      const r = await apiPost('/api/pipeline-bot/auto-heal-all');
      const fixed = (r.actions || []).filter(a => a.fixed).length;
      toast.success(`Healing complete — ${fixed} safe fix(es) applied`);
      await fetchAll();
    } catch (e) {
      toast.error(`Auto-heal failed: ${e.message}`);
    } finally {
      setRunningFix(false);
    }
  };

  const handleToggle = async (checked) => {
    try {
      await apiPost(`/api/pipeline-bot/toggle?enabled=${checked}`);
      toast.success(checked ? 'Auto-heal enabled' : 'Auto-heal paused');
      await fetchAll();
    } catch (e) {
      toast.error('Failed to toggle bot');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const issues = diagnostics?.issues || [];

  return (
    <div data-testid="admin-pipeline-bot" className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-3">
            <Bot className="h-8 w-8 text-violet-600" />
            Pipeline Healing Bot
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Always-on background monitor that diagnoses and repairs the OneDrive call processing pipeline. Safe fixes apply automatically; destructive actions need your confirmation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="refresh-bot-btn"
            variant="outline"
            size="sm"
            onClick={fetchAll}
          >
            <RefreshCw className="h-4 w-4 mr-1" />Refresh
          </Button>
          <Button
            data-testid="auto-heal-all-btn"
            size="sm"
            onClick={handleAutoHealAll}
            disabled={runningFix}
            className="bg-violet-600 hover:bg-violet-700"
          >
            {runningFix ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
            Heal everything safe
          </Button>
        </div>
      </div>

      {/* Status card */}
      <StatusCard
        status={status?.status || 'idle'}
        lastRun={status?.last_run_at}
        statsObj={status?.stats}
        isEnabled={status?.is_enabled ?? true}
        onToggle={handleToggle}
      />

      {/* Tabs: live diagnostics + activity + failures */}
      <Tabs defaultValue="diagnostics" className="w-full">
        <TabsList>
          <TabsTrigger data-testid="tab-diagnostics" value="diagnostics">
            Live Diagnostics
            {issues.length > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{issues.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-failures" value="failures">
            Why calls fail
            {failures?.count > 0 && (
              <Badge className="ml-2 bg-orange-500 text-white">{failures.count}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-activity" value="activity">Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="diagnostics" className="space-y-3 mt-4">
          {issues.length === 0 ? (
            <Card data-testid="all-clear-card" className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20">
              <CardContent className="p-10 text-center">
                <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No issues detected</h3>
                <p className="text-sm text-slate-500 mt-1">
                  All 8 pipeline checks are healthy. The bot will keep monitoring every 60s.
                </p>
              </CardContent>
            </Card>
          ) : (
            issues.map(issue => (
              <IssueCard
                key={issue.issue_id}
                issue={issue}
                onHeal={handleHeal}
                isHealing={healingId === issue.issue_id}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="failures" className="mt-4 space-y-3">
          <Card data-testid="failures-summary">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                Recent failure causes ({failures?.count ?? 0})
              </CardTitle>
              <CardDescription>
                Grouped by root cause so you can see if the issue is OneDrive, AI, network, or app logic.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {failures?.count > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    {Object.entries(failures.buckets || {}).map(([key, count]) => (
                      <div
                        key={key}
                        data-testid={`failure-bucket-${key}`}
                        className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-900/50"
                      >
                        <p className="text-xs text-slate-500 uppercase tracking-wider">
                          {key.replace(/_/g, ' ')}
                        </p>
                        <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-1">
                          {count}
                        </p>
                      </div>
                    ))}
                  </div>
                  <ScrollArea className="h-[400px] pr-3">
                    {(failures.failures || []).map((f) => (
                      <div
                        key={f.id}
                        data-testid={`failure-row-${f.id}`}
                        className="py-3 border-b border-slate-100 dark:border-slate-800 last:border-0"
                      >
                        <div className="flex items-start gap-3">
                          <FileX className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                              {f.filename || f.id}
                            </p>
                            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 break-words">
                              {f.processing_error}
                            </p>
                            <p className="text-[11px] text-slate-500 mt-1">
                              {f.retry_count ? `${f.retry_count} retries · ` : ''}
                              {formatRelative(f.processed_at || f.processing_started_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              ) : (
                <div className="text-center py-10">
                  <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No failed calls in the database.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card data-testid="activity-log">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Recent activity ({activity.length})
              </CardTitle>
              <CardDescription>
                Every diagnostic and heal action — auto and manual — is logged here.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activity.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No activity yet.</p>
              ) : (
                <ScrollArea className="h-[500px] pr-3">
                  {activity.map((ev, i) => <ActivityRow key={i} ev={ev} />)}
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
